import type { BusState, CrossCar, TrafficCar } from '../game/RunState';

/**
 * What a crash does to the other vehicle: it isn't removed. It gets shoved, swings round a little,
 * stops while the drivers sort it out, then drives on. Barriers get knocked over to the side and stay there.
 */
export const KNOCK = {
  /** Seconds the other car stands still after the crash. */
  stunned: 2.4,
  /** Seconds it can't be hit again (so one crash isn't counted twice). */
  noContact: 4,
  /** How far it gets pushed forward when hit from behind, metres. */
  shove: 1.6,
  /** How far it slides sideways, metres. */
  slide: 0.45,
  /** How far it swings round, radians. */
  spin: 0.32,
  /** How quickly the swing straightens out once it drives on, per second. */
  straighten: 1.8,
} as const;

/** The trotro hits a traffic car (or barrier). */
export function knockTraffic(car: TrafficCar, bus: BusState): void {
  const side = Math.sign(car.x - bus.x) || (Math.random() < 0.5 ? -1 : 1);
  if (car.kind.hoppable) {
    // A knocked-over barrier lies at the edge of its lane for good.
    car.x += side * 1.3;
    car.spin = side * 1.1;
    car.noContact = Infinity;
    return;
  }
  if (car.s > bus.s) car.s += KNOCK.shove;
  car.x += side * KNOCK.slide;
  car.spin = side * KNOCK.spin;
  car.stunned = KNOCK.stunned;
  car.noContact = KNOCK.noContact;
}

/** The trotro hits a car crossing at a junction: it stops in the junction for a moment, then carries on. */
export function knockCrossCar(car: CrossCar): void {
  car.stunned = KNOCK.stunned;
  car.noContact = KNOCK.noContact;
  car.spin = (Math.random() < 0.5 ? -1 : 1) * KNOCK.spin;
}

/** Counts a knocked vehicle's timers down. Returns true while it should stay put. */
export function recoverFromKnock(car: { stunned?: number; noContact?: number; spin?: number }, dt: number): boolean {
  if (car.noContact !== undefined && Number.isFinite(car.noContact)) car.noContact = Math.max(0, car.noContact - dt);
  if (car.stunned && car.stunned > 0) {
    car.stunned = Math.max(0, car.stunned - dt);
    return true;
  }
  if (car.spin && Number.isFinite(car.noContact ?? 0)) car.spin *= Math.exp(-KNOCK.straighten * dt);
  return false;
}

export function canBeHit(car: { noContact?: number }): boolean {
  return !car.noContact || car.noContact <= 0;
}
